/* eslint-disable jsx-a11y/heading-has-content */
/* eslint-disable jsx-a11y/anchor-has-content */
import { useEffect, useRef, useState } from 'react';
import { FiArrowLeft, FiZap, FiSearch } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { useHubSpotPortals } from '../hooks/useHubSpotPortals';
import { distributionExplanationApi } from '../services/api';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const isEmptyRecordPayload = (record) => {
  if (record == null) return true;
  if (typeof record !== 'string') return false;

  const normalized = record.trim().toLowerCase();
  return normalized === '' || normalized === 'null' || normalized === 'undefined';
};

export default function DistributionExplanationAgent() {
  const { selectedPortal } = useHubSpotPortals();
  const [objectId, setObjectId] = useState('');
  const [objectType, setObjectType] = useState('DEAL');
  const analysisMode = 'analytics-record';
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [explanation, setExplanation] = useState(null);

  // Optional: show status while polling
  const [statusMessage, setStatusMessage] = useState(null);

  const pollIntervalRef = useRef(null);

  const clearPolling = () => {
    if (pollIntervalRef.current) {
      window.clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      clearPolling();
    };
  }, []);

// Replace your handleSubmit function with this updated version

const handleSubmit = async (e) => {
  e.preventDefault();

  const trimmedObjectId = objectId.trim();

  if (!trimmedObjectId) {
    setError('Please enter an Object ID');
    return;
  }

  if (!selectedPortal?.portal_id) {
    setError('No portal selected. Please select a portal first.');
    return;
  }

  clearPolling();
  setLoading(true);
  setError(null);
  setExplanation(null);
  setStatusMessage('Fetching distribution record...');

  const portalId = selectedPortal.portal_id;

  try {
    // Step 1: Fetch distribution record
    const recordData = await distributionExplanationApi.fetchDistributionRecord({
      portalId,
      objectId: trimmedObjectId,
      objectType,
    });

    const record = recordData?.outputFields?.record;

    if (isEmptyRecordPayload(record)) {
      setError('No distribution record found for this object.');
      setLoading(false);
      setStatusMessage(null);
      return;
    }

    // Parse the record if it's a string
    let parsedRecord;
    try {
      parsedRecord = typeof record === 'string' ? JSON.parse(record) : record;
    } catch (parseErr) {
      console.error('Error parsing record:', parseErr);
      setError('Error parsing distribution record.');
      setLoading(false);
      setStatusMessage(null);
      return;
    }

    if (!parsedRecord || typeof parsedRecord !== 'object') {
      setError('No distribution record found for this object.');
      setLoading(false);
      setStatusMessage(null);
      return;
    }

    // If explanation already exists in the analytics record, just show it and stop.
    const cachedExplanation = parsedRecord?.explanation ?? null;

    if (cachedExplanation) {
      console.log('Explanation already exists in record');
      setExplanation(cachedExplanation);
      setLoading(false);
      setStatusMessage(null);
      return;
    }

    setStatusMessage('Starting explanation job...');

    // Step 2: Kick off async explanation job
    const explanationStartResp = await distributionExplanationApi.explainDistribution(
      parsedRecord,
      analysisMode
    );

    const jobId =
      explanationStartResp?.outputFields?.jobId ||
      explanationStartResp?.outputFields?.jobID ||
      explanationStartResp?.jobId ||
      null;

    if (!jobId) {
      setError('Failed to start explanation job (no jobId returned)');
      setLoading(false);
      setStatusMessage(null);
      return;
    }

    console.log('Job started with ID:', jobId);
    setStatusMessage(`Job ${jobId} started. Generating explanation...`);

    // Step 3: Poll job status for up to 60 seconds
    const startTime = Date.now();
    const timeoutMs = 60_000;
    const pollEveryMs = 2_000;

    pollIntervalRef.current = window.setInterval(async () => {
      try {
        const elapsed = Date.now() - startTime;

        if (elapsed >= timeoutMs) {
          clearPolling();
          setLoading(false);
          setStatusMessage(null);
          setError('Timed out waiting for the explanation (60s). Please try again.');
          return;
        }

        console.log(`Polling job ${jobId}... (${Math.round(elapsed / 1000)}s elapsed)`);
        setStatusMessage(`Generating explanation... (${Math.round(elapsed / 1000)}s)`);

        // Poll the job status endpoint
        const jobStatusResponse = await distributionExplanationApi.getJobStatus(jobId);
        
        // Extract status from the response
        const jobStatus = jobStatusResponse?.outputFields || jobStatusResponse;

        console.log('Job status:', jobStatus);

        if (jobStatus.status === 'completed' && jobStatus.explanation) {
          clearPolling();
          setExplanation(jobStatus.explanation);
          setLoading(false);
          setStatusMessage(null);
          console.log('Explanation completed successfully');
        } else if (jobStatus.status === 'failed') {
          clearPolling();
          setLoading(false);
          setStatusMessage(null);
          const errMsg = jobStatus.errorMessage || 'Explanation generation failed. Please try again.';
          setError(errMsg);
          console.error('Job failed:', errMsg);
        } else if (jobStatus.status === 'processing') {
          // Still processing, keep polling
          console.log('Job still processing...');
        } else if (jobStatus.status === 'queued') {
          // Still queued, keep polling
          console.log('Job still queued...');
        }
      } catch (pollErr) {
        console.error('Polling error:', pollErr);
        
        // If we get a 404, the job might not exist
        if (pollErr?.response?.status === 404) {
          clearPolling();
          setLoading(false);
          setStatusMessage(null);
          setError('Job not found. Please try again.');
        }
        // For other errors, keep polling until timeout
      }
    }, pollEveryMs);
  } catch (err) {
    console.error('Error fetching distribution explanation:', err);
    setError(
      err?.response?.data?.message ||
        err?.message ||
        'Failed to fetch distribution explanation. Please try again.'
    );
    setLoading(false);
    setStatusMessage(null);
    clearPolling();
  }
};

  const handleReset = () => {
    clearPolling();
    setObjectId('');
    setObjectType('DEAL');
    setError(null);
    setExplanation(null);
    setStatusMessage(null);
    setLoading(false);
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="mb-4">
          <Link
            to="/agents"
            className="inline-flex items-center text-gray-500 hover:text-gray-700 mb-3"
          >
            <FiArrowLeft className="w-4 h-4 mr-1" />
            Back to Agents
          </Link>
          <div>
            <div className="flex items-center mb-1">
              <FiZap className="w-6 h-6 text-blue-600 mr-3" />
              <h1 className="text-2xl font-bold text-gray-900">Distribution Explanation Agent</h1>
            </div>
            <p className="text-gray-600">
              Get an explanation of how team selection and owner assignment was made
            </p>
          </div>
        </div>

        {selectedPortal && (
          <p className="text-sm text-blue-700">
            Analyzing distributions for portal:{' '}
            <span className="font-semibold">{selectedPortal.hub_domain}</span>
            <span className="ml-1 text-gray-600">(ID: {selectedPortal.portal_id})</span>
          </p>
        )}
      </div>

      {/* Input Form */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Lookup Distribution</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Object ID Input */}
            <div>
              <label htmlFor="objectId" className="block text-sm font-medium text-gray-700 mb-1">
                Object ID *
              </label>
              <input
                type="text"
                id="objectId"
                value={objectId}
                onChange={(e) => setObjectId(e.target.value)}
                placeholder="Enter Object ID (e.g., 40282520245)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={loading}
              />
            </div>

            {/* Object Type Dropdown */}
            <div>
              <label htmlFor="objectType" className="block text-sm font-medium text-gray-700 mb-1">
                Object Type *
              </label>
              <select
                id="objectType"
                value={objectType}
                onChange={(e) => setObjectType(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                disabled={loading}
              >
                <option value="CONTACT">CONTACT</option>
                <option value="COMPANY">COMPANY</option>
                <option value="DEAL">DEAL</option>
                <option value="TICKET">TICKET</option>
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center space-x-3">
            <button
              type="submit"
              disabled={loading || !selectedPortal}
              className="flex items-center bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg shadow-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FiSearch className="w-4 h-4 mr-2" />
              {loading ? 'Analyzing...' : 'Get Explanation'}
            </button>

            {(explanation || error) && !loading && (
              <button
                type="button"
                onClick={handleReset}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Reset
              </button>
            )}
          </div>
        </form>

        {!selectedPortal && (
          <div className="mt-4 bg-yellow-50 border border-yellow-200 rounded-md p-4">
            <p className="text-sm text-yellow-800">
              Please select a HubSpot portal from the dropdown menu to continue.
            </p>
          </div>
        )}
      </div>

      {/* Loading Indicator */}
      {loading && (
        <div className="bg-white shadow-sm border border-gray-200 rounded-lg p-8">
          <div className="flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-4"></div>
            <p className="text-gray-600 text-lg">Analyzing distribution...</p>
            {statusMessage ? (
              <p className="text-gray-500 text-sm mt-2">{statusMessage}</p>
            ) : (
              <p className="text-gray-500 text-sm mt-2">This may take a few moments</p>
            )}
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && !loading && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start">
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Explanation Display */}
      {explanation && !loading && (
        <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
          <div className="bg-blue-50 border-b border-blue-200 px-6 py-4">
            <h2 className="text-lg font-semibold text-gray-900">Distribution Explanation</h2>
            <p className="text-sm text-gray-600 mt-1">
              Object ID: <span className="font-medium">{objectId}</span> | Type:{' '}
              <span className="font-medium">{objectType}</span>
            </p>
          </div>

          <div className="px-6 py-6">
            <div className="prose prose-blue max-w-none">
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                components={{
                  h1: ({ node, ...props }) => (
                    <h1 className="text-2xl font-bold text-gray-900 mb-4 mt-6" {...props} />
                  ),
                  h2: ({ node, ...props }) => (
                    <h2 className="text-xl font-bold text-gray-900 mb-3 mt-5" {...props} />
                  ),
                  h3: ({ node, ...props }) => (
                    <h3 className="text-lg font-semibold text-gray-900 mb-2 mt-4" {...props} />
                  ),
                  h4: ({ node, ...props }) => (
                    <h4 className="text-base font-semibold text-gray-900 mb-2 mt-3" {...props} />
                  ),
                  p: ({ node, ...props }) => (
                    <p className="text-gray-700 mb-3 leading-relaxed" {...props} />
                  ),
                  ul: ({ node, ...props }) => (
                    <ul className="list-disc list-inside mb-3 space-y-1 text-gray-700" {...props} />
                  ),
                  ol: ({ node, ...props }) => (
                    <ol className="list-decimal list-inside mb-3 space-y-1 text-gray-700" {...props} />
                  ),
                  li: ({ node, ...props }) => <li className="text-gray-700" {...props} />,
                  strong: ({ node, ...props }) => (
                    <strong className="font-semibold text-gray-900" {...props} />
                  ),
                  em: ({ node, ...props }) => <em className="italic text-gray-800" {...props} />,
                  code: ({ node, inline, ...props }) =>
                    inline ? (
                      <code
                        className="bg-gray-100 text-red-600 px-1.5 py-0.5 rounded text-sm font-mono"
                        {...props}
                      />
                    ) : (
                      <code
                        className="block bg-gray-100 text-gray-800 p-3 rounded text-sm font-mono overflow-x-auto mb-3"
                        {...props}
                      />
                    ),
                  pre: ({ node, ...props }) => (
                    <pre className="bg-gray-100 p-4 rounded mb-3 overflow-x-auto" {...props} />
                  ),
                  blockquote: ({ node, ...props }) => (
                    <blockquote className="border-l-4 border-blue-500 pl-4 italic text-gray-700 mb-3" {...props} />
                  ),
                  a: ({ node, ...props }) => (
                    <a className="text-blue-600 hover:text-blue-800 underline" {...props} />
                  ),
                  table: ({ node, ...props }) => (
                    <table className="min-w-full border border-gray-300 mb-3" {...props} />
                  ),
                  thead: ({ node, ...props }) => <thead className="bg-gray-100" {...props} />,
                  tbody: ({ node, ...props }) => (
                    <tbody className="divide-y divide-gray-200" {...props} />
                  ),
                  tr: ({ node, ...props }) => <tr className="border-b border-gray-200" {...props} />,
                  th: ({ node, ...props }) => (
                    <th className="px-4 py-2 text-left text-sm font-semibold text-gray-900 border border-gray-300" {...props} />
                  ),
                  td: ({ node, ...props }) => (
                    <td className="px-4 py-2 text-sm text-gray-700 border border-gray-300" {...props} />
                  ),
                }}
              >
                {explanation}
              </ReactMarkdown>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
